import { useSidePanel } from '@/components/app/side-panel';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCampaignAdInsights, useListCampaignInsights } from '../../api';
import type { Campaign, CampaignInsightsParams } from '../../api/types';
import { CampaignDestinations } from './campaign-destinations';
import { type MetricTotals, formatCampaignMoney } from './campaign-metrics';
import { getCampaignDestinationLabels } from './campaign-presentation';
import { MetricLabel } from './metric-label';

type MetricRow = [label: string, value: string];

/**
 * The metrics a list row does not show. CPC, CPM, Clicks and Frequency live
 * on the row itself, so this panel carries everything else.
 */
function MetricsPanel({
  kicker,
  title,
  badges,
  period,
  groups,
  failed,
  onRetry,
}: {
  kicker: string;
  title: string;
  badges?: ReactNode;
  period: string;
  groups: { title: string; metrics: MetricRow[] }[];
  failed: boolean;
  onRetry: () => void;
}) {
  const { close } = useSidePanel();
  return (
    <>
      <header className="border-b px-6 py-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {kicker}
          </p>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close metrics"
            onClick={close}
          >
            <X className="size-4" />
          </Button>
        </div>
        <h2 className="mt-2 text-xl font-semibold tracking-tight">{title}</h2>
        {badges && <div className="mt-3">{badges}</div>}
        <p className="mt-4 text-xs text-muted-foreground">{period}</p>
      </header>
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {failed ? (
          <div className="mb-6 flex items-center justify-between gap-3 text-sm">
            <span>Performance data unavailable</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Retry
            </Button>
          </div>
        ) : null}
        <div className="space-y-7">
          {groups.map((group) => (
            <section key={group.title}>
              <h3 className="mb-2 text-sm font-semibold">{group.title}</h3>
              <dl className="divide-y divide-border/60">
                {group.metrics.map(([label, value]) => (
                  <div
                    key={label}
                    className="flex items-center justify-between gap-4 py-3 text-sm"
                  >
                    <dt className="text-muted-foreground">
                      <MetricLabel label={label} />
                    </dt>
                    <dd className="font-medium tabular-nums">{value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
        <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
          Metrics reported by Meta for this period. A dash means the metric is
          unavailable or has no results to calculate a cost.
        </p>
      </div>
    </>
  );
}

/** Groups shared by campaigns and ads; `costs` lets campaigns add Budget. */
function metricGroups({
  totals,
  currency,
  loading,
  failed,
  costs = [],
}: {
  totals?: MetricTotals;
  currency?: string;
  loading: boolean;
  failed: boolean;
  costs?: MetricRow[];
}) {
  const show = (format: () => string) =>
    loading ? '…' : failed || !totals ? '—' : format();
  const count = (value: number | undefined) =>
    show(() =>
      value === undefined
        ? '—'
        : value.toLocaleString(undefined, { maximumFractionDigits: 2 })
    );
  const leads = totals?.leads;
  return [
    {
      title: 'Results',
      metrics: [['Leads', count(leads)]] as MetricRow[],
    },
    {
      title: 'Delivery',
      metrics: [
        ['Views (impressions)', count(totals?.impressions)],
        ['Reach', count(totals?.reach)],
      ] as MetricRow[],
    },
    {
      title: 'Engagement',
      metrics: [
        [
          'CTR',
          show(() =>
            totals?.ctr === undefined
              ? '—'
              : `${totals.ctr.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
          ),
        ],
      ] as MetricRow[],
    },
    {
      title: 'Costs',
      metrics: [
        [
          'Spent',
          show(() => formatCampaignMoney(totals?.spend ?? 0, currency)),
        ],
        ...costs,
      ] as MetricRow[],
    },
  ];
}

function budgetLabel(campaign: Campaign, currency?: string): string {
  const daily = Number(campaign.dailyBudget ?? 0);
  const lifetime = Number(campaign.lifetimeBudget ?? 0);
  if (daily > 0) return `${formatCampaignMoney(daily, currency)} daily average`;
  if (lifetime > 0)
    return `${formatCampaignMoney(lifetime, currency)} lifetime`;
  return '—';
}

export function CampaignMetricsPanel({
  campaign,
  params,
  period,
}: { campaign: Campaign; params: CampaignInsightsParams; period: string }) {
  const { insightsByCampaignId, isLoading, isError, refetch } =
    useListCampaignInsights(params);
  // No entry after a successful load means no delivery in the period.
  const summary = insightsByCampaignId.get(campaign.id);
  const currency = summary?.currency ?? campaign.currency;
  const totals = summary?.totals ?? (!isLoading && !isError ? {} : undefined);
  return (
    <MetricsPanel
      kicker="Campaign performance"
      title={campaign.name}
      badges={
        <CampaignDestinations labels={getCampaignDestinationLabels(campaign)} />
      }
      period={period}
      failed={isError}
      onRetry={() => void refetch()}
      groups={metricGroups({
        totals,
        currency,
        loading: isLoading,
        failed: isError,
        costs: [['Budget', budgetLabel(campaign, currency)]],
      })}
    />
  );
}

export function AdMetricsPanel({
  campaignId,
  metaAdId,
  adName,
  currency,
}: {
  campaignId: string;
  metaAdId: string | null | undefined;
  adName: string;
  currency?: string;
}) {
  const {
    totals,
    currency: insightsCurrency,
    isLoading,
    isError,
    refetch,
  } = useCampaignAdInsights(campaignId, metaAdId);
  return (
    <MetricsPanel
      kicker="Ad performance"
      title={adName}
      period="Lifetime"
      failed={isError}
      onRetry={() => void refetch()}
      groups={metricGroups({
        totals,
        currency: insightsCurrency ?? currency,
        loading: isLoading,
        failed: isError,
      })}
    />
  );
}
